/**
 * RECENTLY VIEWED — <recently-viewed-record> and <recently-viewed>.
 * docs/09 §11 · docs/35 C6 · docs/47 §18.5.
 *
 * Two elements, one file, because they share the storage contract and
 * nothing else. The PDP records; the collection, product and cart templates
 * display.
 *
 * IIFE-wrapped — the PDP renders BOTH (it records the current product and
 * displays the previous ones), so this tag is emitted twice on that route
 * and executes twice (docs/54 §5.6).
 */
(function () {
  if (customElements.get('recently-viewed')) return;

  /**
   * Product IDS, not handles. A handle changes when a merchant renames a
   * product; an id never does. A stale handle in someone's localStorage
   * would drop that product from their history permanently, and they would
   * have no way to know why.
   */
  const KEY = 'dramour:recently-viewed';
  const MAX = 20;

  function read() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      // Coerce and filter: a malformed value from an older build, or from
      // another tab, must not take the section down.
      return Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  function write(ids) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX)));
    } catch {
      /* private mode or a full quota — the history just does not persist */
    }
  }

  /* ---------------------------------------------------------------------- *
   * <recently-viewed-record> — the PDP's writer
   * ---------------------------------------------------------------------- */
  class RecentlyViewedRecord extends HTMLElement {
    connectedCallback() {
      const id = Number(this.dataset.productId);
      if (!id) return;
      // Most recent first, and de-duplicated: viewing a product again should
      // move it to the front rather than add a second entry.
      write([id, ...read().filter((existing) => existing !== id)]);
    }
  }

  /* ---------------------------------------------------------------------- *
   * <recently-viewed> — the display
   *
   * Section Rendering API against /search, because that is the only route
   * whose context can be "these specific product ids". The card is rendered
   * by Liquid in the response — docs/47 §18.5: never duplicate card markup
   * in JS.
   * ---------------------------------------------------------------------- */
  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      this.render();
    }

    async render() {
      const limit = Number(this.dataset.limit) || 5;

      /**
       * The product being looked at right now is not "recently viewed" — it
       * is currently viewed. The record element has already written it by
       * the time this runs, so it has to come back out, or the PDP shows the
       * visitor the page they are on.
       */
      const current = Number(document.querySelector('recently-viewed-record')?.dataset.productId);
      const ids = read()
        .filter((id) => id !== current)
        .slice(0, limit);

      if (ids.length === 0) return; // stays hidden — docs/09 §11's first visit

      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url = `${window.Shopify?.routes?.root || '/'}search?section_id=${encodeURIComponent(
        this.dataset.sectionId,
      )}&type=product&q=${encodeURIComponent(query)}`;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Recently viewed: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        const source = doc.querySelector('.recently-viewed__grid');
        const target = this.querySelector('.recently-viewed__grid');
        if (!source || !target || source.children.length === 0) return;

        /**
         * Shopify's search returns results in RELEVANCE order, which for an
         * `id:` query is arbitrary. The visitor's own order is the point of
         * the section, so the cards are re-sorted into it here.
         */
        const items = [...source.children];
        const rank = new Map(ids.map((id, index) => [String(id), index]));
        items.sort((a, b) => {
          const ai = rank.get(a.querySelector('[data-product-id]')?.dataset.productId ?? '');
          const bi = rank.get(b.querySelector('[data-product-id]')?.dataset.productId ?? '');
          return (ai ?? 99) - (bi ?? 99);
        });

        target.replaceChildren(...items.map((item) => document.importNode(item, true)));
        this.hidden = false;
      } catch (error) {
        // A failed fetch leaves the section hidden, which is the same thing
        // a first-time visitor sees. Nothing breaks; nothing empty renders.
        console.error(error);
      }
    }
  }

  customElements.define('recently-viewed-record', RecentlyViewedRecord);
  customElements.define('recently-viewed', RecentlyViewed);
})();
